import { jwtDecode } from "jwt-decode";
import axios from "./axios";

/**
 * Checks if the provided JWT token is valid (not expired).
 *
 * @param {string} authToken - The JWT token to validate.
 * @returns {boolean} - Returns `true` if the token is valid, otherwise `false`.
 */
export const isTokenValid = (authToken) => {
  if (typeof authToken !== "string") {
    console.error("Invalid token format.");
    return false;
  }

  try {
    const decoded = jwtDecode(authToken);
    const currentTime = Date.now() / 1000; // Current time in seconds since epoch

    return decoded.exp > currentTime;
  } catch (err) {
    console.error("Failed to decode token:", err);
    return false;
  }
};

/**
 * Sets or removes the authentication token in local storage and axios headers.
 *
 * @param {string} [authToken] - The JWT token to set. If `undefined` or `null`, the session will be cleared.
 */
export const setSession = (authToken) => {
  if (typeof authToken === "string" && authToken.trim() !== "") {
    // Store token in local storage and set authorization header for axios
    localStorage.setItem("authToken", authToken);
    axios.defaults.headers.common.Authorization = `Bearer ${authToken}`;
  } else {
    // Remove token from local storage and delete authorization header from axios
    localStorage.removeItem("authToken");
    delete axios.defaults.headers.common.Authorization;
  }
};

/**
 * Gets the current authentication token from local storage.
 *
 * @returns {string|null} - The stored JWT token or null if not found.
 */
export const getToken = () => {
  return localStorage.getItem("authToken");
};

/**
 * Extracts user ID from the JWT token.
 *
 * @returns {string|null} - The user ID from the token or null if not found.
 */
export const getUserIdFromToken = () => {
  try {
    const token = getToken();
    if (!token) return null;
    
    const decoded = jwtDecode(token);
    return decoded.nameid || decoded.sub || decoded.userId || null;
  } catch (error) {
    console.error("Error extracting user ID from token:", error);
    return null;
  }
};

/**
 * Extracts company ID from the JWT token.
 *
 * @returns {string|null} - The company ID from the token or null if not found.
 */
export const getCompanyIdFromToken = () => {
  try {
    const token = getToken();
    if (!token) return null;
    
    const decoded = jwtDecode(token);
    return decoded.companyid || decoded.companyId || decoded.CompanyId || null;
  } catch (error) {
    console.error("Error extracting company ID from token:", error);
    return null;
  }
};

/**
 * Gets the decoded token payload.
 *
 * @returns {object|null} - The decoded token payload or null if invalid.
 */
export const getDecodedToken = () => {
  try {
    const token = getToken();
    if (!token) return null;
    
    return jwtDecode(token);
  } catch (error) {
    console.error("Error decoding token:", error);
    return null;
  }
};

/**
 * Checks if the current token is expired.
 *
 * @returns {boolean} - Returns true if token is expired or invalid.
 */
export const isTokenExpired = () => {
  return !isTokenValid(getToken());
};

/**
 * Refreshes the session by checking token validity.
 * Returns true if session is valid, false otherwise.
 *
 * @returns {boolean} - Session validity status.
 */
export const refreshSession = () => {
  const token = getToken();
  if (!token) return false;
  
  return isTokenValid(token);
};
